import React, { useEffect, useRef } from "react";
import Map, {
  CircleLayer,
  LineLayer,
  Layer,
  Source,
  MapRef,
} from "react-map-gl";
import { useRequiredData } from "../../../contextApis/RequiredDataContext";
import "./MapboxMap.css";

/* global console */

interface MapboxMapProps {
  directionCoordinates?: number[][];
  fitBoundsPadding?: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

const mapsCenter = {
  longitude: -95.9208507044446,
  latitude: 41.144255437575126,
  zoom: 3,
};

const mapsTemplate = "mapbox://styles/logytext1/clzv6sccq00lc01nzhfj9hucm";

const directionLayer: LineLayer = {
  id: "route-line",
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "butt",
  },
  paint: {
    "line-color": "#C62906",
    "line-width": 4,
  },
};

const markersLayer: CircleLayer = {
  id: "route-markets",
  type: "circle",
  paint: {
    "circle-radius": 6,
    "circle-color": "#C62906",
  },
};

const getDirectionGeoJson = (coordinates: number[][]) => ({
  type: "FeatureCollection" as const,
  features: [
    {
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "LineString" as const,
        coordinates,
      },
    },
  ],
});

const getMarkersGeoJson = (origin: number[], destination: number[]) => ({
  type: "FeatureCollection" as const,
  features: [
    {
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "Point" as const,
        coordinates: origin,
      },
    },
    {
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "Point" as const,
        coordinates: destination,
      },
    },
  ],
});

const calculateBoundingBox = (
  coordinates: number[][],
): [number, number, number, number] => {
  let minX = coordinates[0][0];
  let minY = coordinates[0][1];
  let maxX = coordinates[0][0];
  let maxY = coordinates[0][1];

  coordinates.forEach(([lon, lat]) => {
    minX = Math.min(minX, lon);
    minY = Math.min(minY, lat);
    maxX = Math.max(maxX, lon);
    maxY = Math.max(maxY, lat);
  });

  return [minX, minY, maxX, maxY];
};

const MapboxMap = ({
  directionCoordinates,
  fitBoundsPadding = {
    top: 12,
    bottom: 12,
    left: 12,
    right: 12,
  },
}: MapboxMapProps) => {
  const mapRef = useRef<MapRef>(null);
  const { mapBoxConfig } = useRequiredData();

  const zoomToRoute = () => {
    try {
      if (directionCoordinates?.length && mapRef.current) {
        const routeBounds = calculateBoundingBox(directionCoordinates);
        mapRef.current.fitBounds(routeBounds, {
          padding: fitBoundsPadding,
        });
      }
    } catch (error) {
      console.error("Error zooming to route:", error);
    }
  };

  useEffect(() => {
    zoomToRoute();
  }, [directionCoordinates]);

  if (!mapBoxConfig?.mapBoxApiKey) return null;

  return (
    <Map
      style={{
        width: "100%",
        height: "100%",
      }}
      initialViewState={mapsCenter}
      mapStyle={mapsTemplate}
      mapboxAccessToken={mapBoxConfig.mapBoxApiKey}
      ref={mapRef}
      maxBounds={[
        [-140, 15],
        [-50, 60],
      ]}
      doubleClickZoom={true}
    >
      {directionCoordinates?.length > 0 && (
        <>
          <Source
            id="route-line"
            type="geojson"
            data={getDirectionGeoJson(directionCoordinates)}
          >
            <Layer {...directionLayer} />
          </Source>
          <Source
            id="route-markers"
            type="geojson"
            data={getMarkersGeoJson(
              directionCoordinates[0],
              directionCoordinates[directionCoordinates.length - 1],
            )}
          >
            <Layer {...markersLayer} />
          </Source>
        </>
      )}
    </Map>
  );
};

export default MapboxMap;
